// Script for re-using the marketplace navbar and footer in WebFlow.
// It's intended to be used as a "Code Embed", here is how the script is used in WebFlow:

/*
<script src="/assets/external/ExternalMarketplace.latest.js"></script>

<script>
  document.addEventListener("DOMContentLoaded", () => {
    ExternalMarketplace.init({
      footerNode: document.querySelector("footer"),
      headerNode: document.getElementById("embed-header"),
      styleSheetUrl:
        "/assets/external/ExternalMarketplace.latest.css",
      segmentWriteKey: "GUz9AOqQpNEDLM0PpL1pg2uaiQ4Sl5sl",
      clerkPublishableKey: "pk_test_XXX",
      reforgeApiKey:
        "426-Production-P454-E967-FRONTEND-bc23132b-0bd0-4d70-838b-0789aaf19c48",
      nimbusUrl: "https://app.vendr.com/nimbus",
      chromeExtensionUrl: "https://chromewebstore.google.com/detail/...",
      algolia: {
        appId: "VYSCARNLSB",
        defaultIndex: "prod_backoffice",
        searchKey: "20d8ebb03c9f93edf1641cd97e5d970b",
      },
      identityV2Route: "https://www.vendr.com/api/identity/v2",
    });

      const header = document.getElementById("external-marketplace-header");
      const pageWrapper = header.closest(".page-wrapper").parentElement;
      pageWrapper.insertBefore(header, pageWrapper.firstChild);
  });
</script>

<div id="embed-header" style="height:116px"></div>
*/

import "~/globals.css";
import "./routes/_marketplace/marketplace.css";

import * as ReactDOM from "react-dom/client";
import { CookieConsent } from "~/components/CookieConsent";
import { Navbar } from "~/components/DesignSystem/Navbar/Navbar";
import { FooterSection } from "~/components/DesignSystem/sections/FooterSection/FooterSection";
import { External } from "~/components/External";
import { RuthCard } from "~/components/RuthCard";
import { CookieConsentProvider } from "~/contexts/cookieConsentContext";
import { NavbarVisibilityProvider } from "~/contexts/navbarVisibilityContext";
import * as AuthSchema from "~/routes/_marketplace.api.identity.v2/schema";

const lookupAuth = async (route?: string) => {
  try {
    const response = await fetch(route ?? "/api/identity/v2", {
      mode: "cors",
      credentials: "include",
    });

    if (!response.ok) {
      return AuthSchema.defaultResponse;
    }

    const body = await response.json();

    const result = AuthSchema.responseDecoder.safeParse(body);

    if (!result.success) {
      return AuthSchema.defaultResponse;
    }

    return result.data;
  } catch {
    return AuthSchema.defaultResponse;
  }
};

const renderInShadowDOM = (args: {
  target: Element;
  react: React.ReactNode;
  styleSheetUrl: string;
  segmentWriteKey?: string;
  reforgeApiKey?: string;
  clerkPublishableKey?: string | null;
  authPromise: Promise<AuthSchema.Response>;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  stickyToTop?: boolean;
  position?: string;
  zIndex?: string;
  id?: string;
}) => {
  const container = document.createElement("div");

  if (args.id) {
    container.id = args.id;
  }

  if (args.stickyToTop) {
    container.style.position = "sticky";
    container.style.top = "0px";
  }

  if (args.position) {
    container.style.position = args.position;
  }

  if (args.zIndex) {
    container.style.zIndex = args.zIndex;
  }

  args.target.replaceWith(container);

  const shadowRoot = container.attachShadow({ mode: "open" });

  const reactRoot = ReactDOM.createRoot(shadowRoot);

  reactRoot.render(
    <External
      styleSheetUrl={args.styleSheetUrl}
      algolia={args.algolia}
      authPromise={args.authPromise}
      clerkPublishableKey={args.clerkPublishableKey}
      reforgeApiKey={args.reforgeApiKey}
      segmentWriteKey={args.segmentWriteKey}
    >
      <link rel="stylesheet" href={args.styleSheetUrl} />

      <style>
        {`.radix-themes:where([data-is-root-theme='true']) {
          min-height: unset;
          background: none;
        }`}
      </style>

      {args.react}
    </External>,
  );
};

export type InitArgs = {
  headerNode?: Element;
  footerNode?: Element;
  ruthCardNode?: Element;
  cookieConsentNode?: Element;
  styleSheetUrl: string;
  nimbusUrl?: string;
  chromeExtensionUrl?: string;
  segmentWriteKey?: string;
  reforgeApiKey?: string;
  clerkPublishableKey?: string;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  identityV2Route?: string;
};

export const init = (args: InitArgs) => {
  const authPromise = lookupAuth(args.identityV2Route);

  if (args.footerNode) {
    renderInShadowDOM({
      id: "external-marketplace-footer",
      target: args.footerNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      authPromise,
      algolia: args.algolia,
      zIndex: "0",
      react: <FooterSection />,
    });
  }

  if (args.headerNode) {
    renderInShadowDOM({
      id: "external-marketplace-header",
      target: args.headerNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      // Only pass Clerk key for Navbar as it'd create a new context for each component resulting
      // in this error:  @clerk/clerk-react: You've added multiple <ClerkProvider> components in your React component tree.
      clerkPublishableKey: args.clerkPublishableKey,
      authPromise,
      algolia: args.algolia,
      stickyToTop: true,
      zIndex: "1001",
      react: (
        <NavbarVisibilityProvider>
          <Navbar
            nimbusUrl={args.nimbusUrl}
            chromeExtensionUrl={args.chromeExtensionUrl}
          />
        </NavbarVisibilityProvider>
      ),
    });
  }

  if (args.ruthCardNode) {
    renderInShadowDOM({
      id: "external-marketplace-ruth-card",
      target: args.ruthCardNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      authPromise,
      algolia: args.algolia,
      react: <RuthCard nimbusUrl={args.nimbusUrl} />,
    });
  }

  if (args.cookieConsentNode) {
    renderInShadowDOM({
      id: "external-marketplace-cookie-consent",
      target: args.cookieConsentNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      authPromise,
      algolia: args.algolia,
      position: "fixed",
      zIndex: "9999",
      react: (
        <CookieConsentProvider>
          <CookieConsent />
        </CookieConsentProvider>
      ),
    });
  }
};
